import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import { getAdminApprovals, approveUser, rejectUser } from "../api";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const DOC_LABELS = [
  { key: "pan", label: "PAN" },
  { key: "aadhaar", label: "Aadhaar" },
  { key: "cheque", label: "Cheque" },
];

export default function AdminApprovals() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectDialogId, setRejectDialogId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [snack, setSnack] = useState(null);

  const fetchApprovals = useCallback(() => {
    setLoading(true);
    getAdminApprovals()
      .then(({ data }) => setUsers(data.users || []))
      .catch((e) =>
        setSnack({
          severity: "error",
          msg: e.response?.data?.error || "Failed to load approvals",
        }),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await approveUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setSnack({ severity: "success", msg: "User approved" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to approve user",
      });
    }
    setProcessing(null);
  };

  const handleReject = async () => {
    if (!rejectDialogId) return;
    const id = rejectDialogId;
    setProcessing(id);
    try {
      await rejectUser(id, rejectReason);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      setRejectDialogId(null);
      setRejectReason("");
      setSnack({ severity: "success", msg: "User rejected" });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to reject user",
      });
    }
    setProcessing(null);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            User Approvals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review KYC documents and approve or reject new users
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ p: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <FactCheckRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No pending approvals
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1040 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Invited By</TableCell>
                  <TableCell>PAN / Aadhaar</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {u.firstName} {u.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize={13}>
                        {(u.roleName || "").replace(/_/g, " ")}
                        {u.roleLevel ? ` (L${u.roleLevel})` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      <Typography variant="body2" fontSize={13}>
                        {u.email || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.mobileNumber || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {u.invitedBy || "—"}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: 12 }}
                      >
                        {u.panCardNumber || "—"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {u.aadharCardNumber || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {DOC_LABELS.map(({ key, label }) => {
                          const url = u.kycDocuments?.[key];
                          return (
                            <Chip
                              key={key}
                              size="small"
                              icon={<DescriptionRoundedIcon />}
                              label={label}
                              clickable={Boolean(url)}
                              disabled={!url}
                              color={url ? "primary" : "default"}
                              variant="outlined"
                              component={url ? "a" : "div"}
                              href={url || undefined}
                              target={url ? "_blank" : undefined}
                              rel={url ? "noopener noreferrer" : undefined}
                              sx={{ fontSize: 11 }}
                            />
                          );
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      {formatDate(u.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleRoundedIcon />}
                          onClick={() => handleApprove(u.id)}
                          disabled={processing === u.id}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          {processing === u.id ? "…" : "Approve"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelRoundedIcon />}
                          onClick={() => setRejectDialogId(u.id)}
                          disabled={processing === u.id}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Reject dialog */}
      <Dialog
        open={Boolean(rejectDialogId)}
        onClose={() => setRejectDialogId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={600}>Reject User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for rejection (optional)"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectDialogId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={processing === rejectDialogId}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity || "info"}
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
